import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, ClipboardCheck, Upload, Copy, Library, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { UseAgainDialog } from "@/components/toolbox/use-again-dialog";
import { PreparedTalks, type PreparedTalk } from "@/components/toolbox/prepared-talks";
import { TalkViewDialog } from "@/components/toolbox/talk-view-dialog";
import { AssignDialog, type AssignSource } from "@/components/toolbox/assign-dialog";
import { PaperUploadDialog } from "@/components/toolbox/paper-upload-dialog";
import { cn } from "@/lib/utils";
import { apiGet } from "@/lib/api";
import { formatDateOnly } from "@/lib/date-only";
import { useGoBack } from "@/hooks/use-go-back";

export const Route = createFileRoute("/_authenticated/toolbox/library")({
  validateSearch: (s: Record<string, unknown>): { tab?: "talks" } => (s.tab === "talks" ? { tab: "talks" } : {}),
  component: ToolboxLibraryPage,
});

interface Held {
  id: string;
  date: string;
  subject: string;
  topics: string | null;
  projectId: string | null;
  projectName: string | null;
  conductedByName: string | null;
  attendees: number;
  status: string;
  isPaper?: boolean;
}

type SortKey = "title" | "date" | "site" | "by" | "attendees" | "type";

const SORT_LABELS: Record<SortKey, string> = { title: "Title", date: "Held date", site: "Jobsite", by: "Conducted by", attendees: "Attendees", type: "Type" };

function typeLabel(it: { kind: "meeting"; r: Held } | { kind: "talk" }): string {
  if (it.kind === "talk") return "Prepared talk";
  return it.r.isPaper ? "Paper copy" : it.r.status === "ready" ? "Signed, not sent" : "Held";
}

function ToolboxLibraryPage() {
  const [q, setQ] = useState("");
  const [projectId, setProjectId] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [again, setAgain] = useState<Held | null>(null);
  const [paperOpen, setPaperOpen] = useState(false);
  const [assigning, setAssigning] = useState<AssignSource | null>(null);
  const [usingTalk, setUsingTalk] = useState<PreparedTalk | null>(null);
  const [viewingTalk, setViewingTalk] = useState<PreparedTalk | null>(null);
  const goBack = useGoBack({ to: "/" });
  const { tab: tabFromLink } = Route.useSearch();
  const [tab, setTab] = useState<"all" | "held" | "paper" | "talks">(tabFromLink === "talks" ? "talks" : "all");

  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (projectId) params.set("projectId", projectId);
  if (from) params.set("from", from);
  if (to) params.set("to", to);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["toolbox", "library", q.trim(), projectId, from, to],
    queryFn: () => apiGet<{ data: Held[]; canRun?: boolean; canAssign?: boolean }>(`/api/toolbox/library?${params}`),
    staleTime: 15_000,
    placeholderData: (prev) => prev,
  });
  const { data: projData } = useQuery({
    queryKey: ["projects", "all-for-toolbox-library"],
    queryFn: () => apiGet<{ data: { id: string; name: string }[] }>("/api/projects?status=all&limit=100"),
    staleTime: 60_000,
  });
  const canRun = !!data?.canRun;
  const canAssign = !!data?.canAssign;
  const meetings = (data?.data ?? []).filter((r) => (tab === "held" ? !r.isPaper : tab === "paper" ? !!r.isPaper : true));

  // The prepared talks sit in the same list (for those who run meetings), found by the same search
  const { data: talkData } = useQuery({
    queryKey: ["toolbox", "talks", q.trim()],
    queryFn: () => apiGet<{ data: PreparedTalk[] }>(`/api/toolbox/talks${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
    enabled: tab === "all" && !projectId && !from && !to,
    staleTime: 15_000,
  });
  type Item = { kind: "meeting"; r: Held; date: string } | { kind: "talk"; t: PreparedTalk; date: string };
  const merged: Item[] = [
    ...meetings.map((r): Item => ({ kind: "meeting", r, date: r.date })),
    ...(tab === "all" ? (talkData?.data ?? []).map((t): Item => ({ kind: "talk", t, date: (t.updatedAt ?? "").slice(0, 10) })) : []),
  ];
  // Sorted by any column; newest held date first by default
  const [sort, setSort] = useState<{ key: SortKey; dir: "asc" | "desc" }>({ key: "date", dir: "desc" });
  const valueOf = (it: Item, key: SortKey): string | number => {
    const m = it.kind === "meeting" ? it.r : null;
    switch (key) {
      case "title": return (m ? m.subject : it.kind === "talk" ? it.t.title : "").toLowerCase();
      case "date": return it.date;
      case "site": return (m?.projectName ?? "").toLowerCase();
      case "by": return (m?.conductedByName ?? "").toLowerCase();
      case "attendees": return m && !m.isPaper ? m.attendees : -1;
      case "type": return typeLabel(it);
    }
  };
  const rows = [...merged].sort((a, b) => {
    const x = valueOf(a, sort.key);
    const y = valueOf(b, sort.key);
    // Empty values (no site, no conductor) always sink to the bottom
    const xe = x === "" || x === -1;
    const ye = y === "" || y === -1;
    if (xe !== ye) return xe ? 1 : -1;
    const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: "base" });
    return (sort.dir === "asc" ? c : -c) || (a.date < b.date ? 1 : a.date > b.date ? -1 : 0);
  });
  const toggleSort = (key: SortKey) => setSort((cur) => (cur.key === key ? { key, dir: cur.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "date" || key === "attendees" ? "desc" : "asc" }));

  return (
    <div className="mx-auto max-w-lg space-y-4 pb-10 md:max-w-5xl">
      <div className="flex items-center gap-2 pt-2">
        <button type="button" onClick={goBack} className="rounded p-1 hover:bg-muted" aria-label="Back"><ArrowLeft className="h-5 w-5" /></button>
        <h1 className="min-w-0 flex-1 text-xl font-bold text-text-primary">Toolbox library</h1>
        {canRun && <Button size="sm" variant="outline" className="gap-1" onClick={() => setPaperOpen(true)}><Upload className="h-4 w-4" /> Upload paper copy</Button>}
      </div>
      <div className="flex w-fit max-w-full flex-wrap gap-1 rounded-lg border border-border bg-muted/30 p-1">
        {([["all", "All"], ["held", "Held toolboxes"], ["paper", "Paper copies"], ["talks", "Prepared talks"]] as [typeof tab, string][]).map(([key, label]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className={cn("rounded-md px-3 py-1.5 text-sm font-medium transition-colors", tab === key ? "bg-surface-raised text-text-primary shadow-sm" : "text-muted-foreground hover:text-text-primary")}
          >
            {label}
          </button>
        ))}
      </div>
      {tab === "talks" && <PreparedTalks canRun={canRun} canAssign={canAssign} />}
      {tab !== "talks" && <p className="text-sm text-muted-foreground">{canRun ? "Held toolboxes, paper copies and prepared talks. Read them, download the PDF, or use one again." : "Held toolboxes, paper copies and prepared talks, from any site. Read them for your knowledge and download the PDF."}</p>}

      {tab !== "talks" && (<>
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search subject, topics, site, who conducted it..." className="h-11 pl-9 text-base md:text-sm" aria-label="Search toolbox meetings" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <div className="min-w-[10rem] flex-1">
          <SearchableSelect size="sm" aria-label="Site" value={projectId} onChange={setProjectId} allLabel="All sites" allValue="" options={(projData?.data ?? []).map((p) => ({ value: p.id, label: p.name }))} />
        </div>
        <input type="date" aria-label="From date" value={from} onChange={(e) => setFrom(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" />
        <input type="date" aria-label="To date" value={to} onChange={(e) => setTo(e.target.value)} className="h-8 rounded-md border border-input bg-background px-2 text-xs" />
        {(q || projectId || from || to) && (
          <button type="button" className="text-xs text-paris-red-text underline underline-offset-2" onClick={() => { setQ(""); setProjectId(""); setFrom(""); setTo(""); }}>Clear</button>
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-muted-foreground">{rows.length} toolbox{rows.length !== 1 ? "es" : ""}</p>
        {/* Phones: sort from a list (on a wide screen click a column heading) */}
        <div className="flex items-center gap-1.5 md:hidden">
          <label htmlFor="tb-sort" className="text-xs text-muted-foreground">Sort by</label>
          <select id="tb-sort" value={sort.key} onChange={(e) => setSort((cur) => ({ ...cur, key: e.target.value as SortKey }))} className="h-8 rounded-md border border-input bg-background px-2 text-xs">
            {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => <option key={k} value={k}>{SORT_LABELS[k]}</option>)}
          </select>
          <button type="button" className="flex h-8 w-8 items-center justify-center rounded-md border border-input" aria-label={sort.dir === "asc" ? "Ascending: press for descending" : "Descending: press for ascending"} onClick={() => setSort((cur) => ({ ...cur, dir: cur.dir === "asc" ? "desc" : "asc" }))}>
            {sort.dir === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {isLoading && <div className="space-y-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)}</div>}
      {isError && <p className="text-sm text-destructive">The library could not be opened.</p>}
      {!isLoading && !isError && rows.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center py-12 text-center">
            <Library className="mb-2 h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm text-muted-foreground">No toolbox meetings found. Meetings appear here once everyone has signed.</p>
          </CardContent>
        </Card>
      )}

      {/* Wide screens: a detailed list, sorted by clicking a heading */}
      {rows.length > 0 && (
        <div className="hidden overflow-x-auto rounded-lg border border-border md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs text-muted-foreground">
              <tr>
                {(["title", "date", "site", "by", "attendees", "type"] as SortKey[]).map((k) => (
                  <th key={k} scope="col" aria-sort={sort.key === k ? (sort.dir === "asc" ? "ascending" : "descending") : "none"} className={cn("px-3 py-2 font-medium", k === "attendees" && "text-right")}>
                    <button type="button" onClick={() => toggleSort(k)} className={cn("inline-flex items-center gap-1 hover:text-foreground", sort.key === k && "text-foreground")}>
                      {SORT_LABELS[k]}
                      {sort.key === k ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ArrowUpDown className="h-3 w-3 opacity-40" />}
                    </button>
                  </th>
                ))}
                <th scope="col" className="px-3 py-2 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.map((it) => it.kind === "talk" ? (
                <tr key={`talk-${it.t.id}`} className="align-top hover:bg-muted/30">
                  <td className="px-3 py-2.5 font-medium">{it.t.title}</td>
                  <td className="px-3 py-2.5 text-xs text-muted-foreground">{it.date ? `Updated ${formatDateOnly(it.date)}` : "-"}</td>
                  <td className="px-3 py-2.5 text-muted-foreground">-</td>
                  <td className="px-3 py-2.5 text-muted-foreground">-</td>
                  <td className="px-3 py-2.5 text-right text-muted-foreground">-</td>
                  <td className="px-3 py-2.5"><span className="rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300">Prepared talk</span></td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setViewingTalk(it.t)}>View</Button>
                      {canRun && <Button size="sm" className="h-8 gap-1 text-xs" onClick={() => setUsingTalk(it.t)}><Copy className="h-3.5 w-3.5" /> Use</Button>}
                      {canAssign && <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setAssigning({ kind: "talk", id: it.t.id, title: it.t.title })}><ClipboardCheck className="h-3.5 w-3.5" /> Assign</Button>}
                    </div>
                  </td>
                </tr>
              ) : ((r) => (
                <tr key={r.id} className="align-top hover:bg-muted/30">
                  <td className="px-3 py-2.5">
                    <Link to="/toolbox/$meetingId" params={{ meetingId: r.id }} className="font-medium hover:text-paris-red-text">{r.subject}</Link>
                    {r.topics && <p className="mt-0.5 line-clamp-1 max-w-xs text-xs text-muted-foreground">{r.topics.replace(/\s+/g, " ")}</p>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2.5">{formatDateOnly(r.date)}</td>
                  <td className="px-3 py-2.5">{r.projectName ?? <span className="text-muted-foreground">-</span>}</td>
                  <td className="px-3 py-2.5">{r.conductedByName ?? <span className="text-muted-foreground">-</span>}</td>
                  <td className="px-3 py-2.5 text-right tabular-nums">{r.isPaper ? <span className="text-muted-foreground">-</span> : r.attendees}</td>
                  <td className="px-3 py-2.5">
                    <span className={cn("rounded-full px-2 py-0.5 text-[11px] font-medium", r.isPaper ? "bg-slate-500/15 text-slate-700 dark:text-slate-300" : canRun && r.status === "ready" ? "bg-blue-500/15 text-blue-700 dark:text-blue-400" : "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400")}>
                      {typeLabel({ kind: "meeting", r: { ...r, status: canRun ? r.status : "held" } })}
                    </span>
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex justify-end gap-1.5">
                      <Link to="/toolbox/$meetingId" params={{ meetingId: r.id }}><Button size="sm" variant="outline" className="h-8 text-xs">View</Button></Link>
                      {canRun && !r.isPaper && <Button size="sm" className="h-8 gap-1 text-xs" onClick={() => setAgain(r)}><Copy className="h-3.5 w-3.5" /> Use again</Button>}
                      {canAssign && !r.isPaper && <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setAssigning({ kind: "meeting", id: r.id, title: r.subject })}><ClipboardCheck className="h-3.5 w-3.5" /> Assign</Button>}
                    </div>
                  </td>
                </tr>
              ))(it.r))}
            </tbody>
          </table>
        </div>
      )}

      {/* Phones: cards */}
      <div className="space-y-2 md:hidden">
        {rows.map((it) => it.kind === "talk" ? (
          <Card key={`talk-${it.t.id}`}>
            <CardContent className="space-y-2 px-4 py-3">
              <p className="text-sm font-semibold">
                {it.t.title}
                <span className="ml-2 rounded-full bg-violet-500/15 px-2 py-0.5 text-[11px] font-medium text-violet-700 dark:text-violet-300">Prepared talk</span>
              </p>
              {it.t.topicsDiscussed && <p className="line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">{it.t.topicsDiscussed}</p>}
              <div className="flex gap-2">
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setViewingTalk(it.t)}>View</Button>
                {canRun && <Button size="sm" className="h-8 gap-1 text-xs" onClick={() => setUsingTalk(it.t)}><Copy className="h-3.5 w-3.5" /> Use this talk</Button>}
                {canAssign && <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setAssigning({ kind: "talk", id: it.t.id, title: it.t.title })}><ClipboardCheck className="h-3.5 w-3.5" /> Assign</Button>}
              </div>
            </CardContent>
          </Card>
        ) : ((r) => (
          <Card key={r.id}>
            <CardContent className="space-y-2 px-4 py-3">
              <Link to="/toolbox/$meetingId" params={{ meetingId: r.id }} className="block">
                <p className="text-sm font-semibold">
                  {r.subject}
                  {r.isPaper && <span className="ml-2 rounded-full bg-slate-500/15 px-2 py-0.5 text-[11px] font-medium text-slate-700 dark:text-slate-300">Paper copy</span>}
                  {canRun && r.status === "ready" && <span className="ml-2 rounded-full bg-blue-500/15 px-2 py-0.5 text-[11px] font-medium text-blue-700 dark:text-blue-400">Signed, not sent</span>}
                </p>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {[r.projectName, formatDateOnly(r.date), r.conductedByName, r.isPaper ? null : `${r.attendees} attendee${r.attendees !== 1 ? "s" : ""}`].filter(Boolean).join(" · ")}
                </p>
                {r.topics && <p className="mt-1 line-clamp-2 whitespace-pre-line text-xs text-muted-foreground">{r.topics}</p>}
              </Link>
              <div className="flex gap-2">
                <Link to="/toolbox/$meetingId" params={{ meetingId: r.id }}>
                  <Button size="sm" variant="outline" className="h-8 text-xs">View</Button>
                </Link>
                {canRun && !r.isPaper && <Button size="sm" className="h-8 gap-1 text-xs" onClick={() => setAgain(r)}><Copy className="h-3.5 w-3.5" /> Use again</Button>}
                {canAssign && !r.isPaper && <Button size="sm" variant="outline" className="h-8 gap-1 text-xs" onClick={() => setAssigning({ kind: "meeting", id: r.id, title: r.subject })}><ClipboardCheck className="h-3.5 w-3.5" /> Assign</Button>}
              </div>
            </CardContent>
          </Card>
        ))(it.r))}
      </div>

      </>)}

      <UseAgainDialog meeting={again} onClose={() => setAgain(null)} />
      <UseAgainDialog kind="talk" meeting={usingTalk ? { id: usingTalk.id, subject: usingTalk.title, projectId: null } : null} onClose={() => setUsingTalk(null)} />
      <TalkViewDialog talk={viewingTalk} onClose={() => setViewingTalk(null)} onUse={canRun ? setUsingTalk : undefined} />
      <AssignDialog source={assigning} onClose={() => setAssigning(null)} />
      <PaperUploadDialog open={paperOpen} onClose={() => setPaperOpen(false)} />
    </div>
  );
}
